import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { v1 } from 'uuid';
import { Table } from 'react-bootstrap';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import {
  getCurrencyFormatter,
  getProductCodeToShowOnReturnModal,
  getShowPricesWithTax,
} from 'reducers/configs/settings';
import { addProduct } from 'actions/ShoppingCart';
import { SaleDocumentRow } from 'types/SalesDocument';
import { getProductsUniversal } from 'actions/productsDB';
import { addWarning } from 'actions/Error';

type Props = {
  saleRows: SaleDocumentRow[];
  saleNumber?: string;
};

const SaleInfoRows: React.FC<Props> = ({ saleRows, saleNumber }) => {
  const isWithTax = useSelector(getShowPricesWithTax);
  const formatCurrency = useSelector(getCurrencyFormatter);
  const codeToShow = useSelector(getProductCodeToShowOnReturnModal);

  const getItemPrice = useCallback(
    (item: SaleDocumentRow) =>
      formatCurrency(!isWithTax ? item.rowNetTotal : item.rowTotal),
    [formatCurrency, isWithTax],
  );

  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { t } = useTranslation(['customer', 'alerts']);

  return (
    <Table
      data-testid="recent-sale-row-products"
      data-test-key={saleNumber}
      borderless
      size="sm"
      style={{ color: 'black' }}
    >
      <thead style={{ background: '#f5f5f5', padding: 0 }}>
        <tr>
          <th style={{ width: '250px', paddingLeft: '1em' }}>
            <span>{t('customerView.purchases.headers.name')}</span>
          </th>
          <th style={{ width: '150px' }}>
            <span>{t(`customerView.purchases.headers.${codeToShow}`)}</span>
          </th>
          <th style={{ width: '50px', textAlign: 'center' }}>
            <span>{t('customerView.purchases.headers.amount')}</span>
          </th>
          <th style={{ textAlign: 'right', paddingRight: '1em' }}>
            <span>{t('customerView.purchases.headers.total')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {saleRows.map(item => (
          <tr
            data-testid="recent-sale-row"
            data-test-key={item.stableRowID}
            key={v1()}
            onClick={async () => {
              const {
                products: [product],
              } = await dispatch(
                getProductsUniversal(
                  {
                    productID: item.productID,
                    recordsOnPage: 1,
                  },
                  { localFirst: true, addToCachedItems: true },
                ),
              );
              if (product.status === 'ARCHIVED') {
                dispatch(
                  addWarning(
                    t('productCouldNotBeAdded', {
                      ns: 'alerts',
                      context: 'invalidStatus',
                      status: 'ARCHIVED',
                    }),
                  ),
                );
                return;
              }
              dispatch(
                addProduct({
                  productID: item.productID,
                  needsWeightPopup: false,
                }),
              );
            }}
          >
            <td style={{ width: '250px', paddingLeft: '1em' }}>
              <span data-testid="item-name">{item.itemName}</span>
            </td>
            <td style={{ width: '150px' }}>
              <span data-testid="item-code">{item[codeToShow]}</span>
            </td>
            <td style={{ width: '50px', textAlign: 'center' }}>
              <span data-testid="item-amount">{item.amount}</span>
            </td>
            <td style={{ textAlign: 'right', paddingRight: '1em' }}>
              <span data-testid="item-price">{getItemPrice(item)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default SaleInfoRows;
